import type {
  ChannelGrant,
  CrmNotificationType,
  NotificationPreferences,
  NotificationScope,
} from '../api/types';

/**
 * What the notification pipeline will actually do for one agent on one channel.
 *
 * The settings screen shows the inputs; this states the outcome. One of the
 * gates is not on the screen at all — read access — so a channel could sit there
 * with every box ticked and still never notify anybody.
 *
 * MIRROR OF services/notifications.js `notify()` plus queue/processors/
 * notifications.js `defaultRecipients()`. It is an explanation, never a gate —
 * the server decides, and a wrong answer here is a confusing sentence, not a
 * leak. Keep the two in step:
 *
 *   recipients   assignee + everyone who may READ the channel
 *   ACCESS       may they read this channel?    ← the security boundary
 *   scope        the whole channel, or only their own — never off
 *   type         per-channel override ?? global ?? default
 */

/** Display order, matching the toggles on the settings card. */
export const REACH_TYPE_ORDER: CrmNotificationType[] = [
  'new_message',
  'conversation_assigned',
  'bot_handoff',
  'conversation_created',
];

/** Short forms for the reach line — the toggle titles are too long to list. */
const TYPE_WORDS: Record<CrmNotificationType, string> = {
  new_message: 'new messages',
  conversation_assigned: 'assignments',
  bot_handoff: 'bot handoffs',
  conversation_created: 'new conversations',
};

/** Server-side defaults — DEFAULT_PREFERENCES in services/notifications.js. */
const DEFAULTS: Record<CrmNotificationType, boolean> = {
  new_message: true,
  conversation_assigned: true,
  bot_handoff: true,
  conversation_created: false,
};

/**
 * Why a channel is silent. `null` on ChannelReach means something arrives.
 * There is no "turned off": a channel cannot be switched off any more, so the
 * only ways to hear nothing are access and every type toggled off.
 */
export type ReachBlock = 'no-access' | 'all-off';

/** How wide the channel reaches this agent, once the gates have run. */
export type ReachWidth =
  /** Every conversation in the channel, however it is assigned — scope 'all'. */
  | 'channel'
  /** Only mine: assigned to them, plus the unassigned. */
  | 'mine';

export interface ChannelReach {
  inboxId: number;
  /** Non-null when nothing at all arrives; `types` is then empty. */
  blocked: ReachBlock | null;
  width: ReachWidth;
  /** The types that will notify, in display order. */
  types: CrmNotificationType[];
  /** The channel carries its own overrides of the global settings. */
  overridden: boolean;
  /**
   * Worth spelling out in a row of its own. False for the ordinary case — the
   * whole channel, the default — where the row's own control already says it
   * and a sentence per channel would be wallpaper rather than a signal.
   */
  notable: boolean;
}

export interface ReachInput {
  inboxId: number;
  /** The agent's grant on this channel; absent = no read access at all. */
  grant: ChannelGrant | undefined;
  /** Global preferences — null while they load. */
  preferences: NotificationPreferences | null;
  /** Only the keys this agent changed for this channel. */
  override: Partial<NotificationPreferences>;
}

/** override ?? global ?? server default — the three-layer merge `notify()` does. */
const effectiveType = (
  type: CrmNotificationType,
  preferences: NotificationPreferences | null,
  override: Partial<NotificationPreferences>,
): boolean => override[type] ?? preferences?.[type] ?? DEFAULTS[type];

/**
 * All or Only mine for this channel, read the way the server reads it: 'all' is
 * the whole channel, and any other stored value is Only mine — including the
 * retired 'off' an agent may hold until the migration that rewrote it reaches
 * their row, which must never read as silence. Nothing stored at all is the
 * default, 'all'.
 */
export const effectiveScope = (
  preferences: NotificationPreferences | null,
  override: Partial<NotificationPreferences> = {},
): NotificationScope => {
  const value = override.scope ?? preferences?.scope;
  return value === undefined || value === 'all' ? 'all' : 'assigned';
};

export function channelReach({ inboxId, grant, preferences, override }: ReachInput): ChannelReach {
  const overridden = Object.keys(override).length > 0;
  const width: ReachWidth = effectiveScope(preferences, override) === 'all' ? 'channel' : 'mine';
  const silent = (blocked: ReachBlock): ChannelReach => ({
    inboxId,
    overridden,
    blocked,
    width,
    types: [],
    notable: true,
  });

  // ACCESS FIRST, exactly as the server orders it: no preference below can
  // widen it, so nothing below is worth computing.
  if (!grant) return silent('no-access');

  const types = REACH_TYPE_ORDER.filter((type) => effectiveType(type, preferences, override));
  if (!types.length) return silent('all-off');
  return { inboxId, overridden, blocked: null, width, types, notable: width === 'mine' };
}

/** One line of plain English for a channel row. */
export function reachLine(reach: ChannelReach): string {
  switch (reach.blocked) {
    case 'no-access':
      return 'Nothing — you do not have access to this channel';
    case 'all-off':
      return 'Nothing — every alert is switched off here';
    default:
      break;
  }
  const what = reach.types.map((type) => TYPE_WORDS[type]).join(', ');
  return reach.width === 'channel'
    ? `${what}, on every conversation here`
    : `${what}, on your conversations and the unassigned ones`;
}

/**
 * The headline: how many channels notify, and why the rest do not. The reasons
 * are counted rather than lumped together — "1 you cannot see" is the answer an
 * agent came for, and it is not one they can fix from this screen.
 */
export interface ReachSummary {
  live: ChannelReach[];
  silent: ChannelReach[];
  noAccess: number;
  allOff: number;
}

export function summarizeReach(rows: ChannelReach[]): ReachSummary {
  const live = rows.filter((row) => row.blocked === null);
  const silent = rows.filter((row) => row.blocked !== null);
  return {
    live,
    silent,
    noAccess: silent.filter((row) => row.blocked === 'no-access').length,
    allOff: silent.filter((row) => row.blocked === 'all-off').length,
  };
}

/** "1 switched off, 2 you cannot see" — the silent half of the headline, or ''. */
export function silentSummary(summary: ReachSummary): string {
  const parts: string[] = [];
  if (summary.allOff) parts.push(`${summary.allOff} switched off`);
  if (summary.noAccess) parts.push(`${summary.noAccess} you cannot see`);
  return parts.join(', ');
}
